import type { EntryValueViewT } from "@/types/database/entries";
import type { InputKeyT } from "@/types/database/forms";

import {
  DatePickerEntryValue,
  DateTimePickerEntryValue,
  DefaultEntryValue,
  DurationEntryValue,
  FootPhotosEntryValue,
  GlucoseSliderEntryValue,
  LongTextEntryValue,
  MultipleChoiceEntryValue,
  NumberEntryValue,
  PainSliderEntryValue,
  PhotoEntryValue,
  RepeatableGroupEntryValue,
  ScaleRatingEntryValue,
  SliderEntryValue,
  YesNoEntryValue,
} from "./input-types";
import { VisaA8EntryValue } from "./input-types";
import { MonthYearEntryValue } from "./input-types/MonthYearEntryValue";
import TriedTreatmentEntryValue from "./input-types/TriedTreatmentEntryValue";
import { VisaG8EntryValue } from "./input-types/VisaG8EntryValue";
import { VisaH8EntryValue } from "./input-types/VisaH8EntryValue";
import { VisaP8EntryValue } from "./input-types/VisaP8EntryValue";

// Extended interface for properties that might not be in the base type
type ExtendedEntryValue = EntryValueViewT & {
  unit?: string;
  duration_time_unit?: string;
  json_value?: Array<
    Array<{
      input_label: string;
      input_id: string;
      input_key: InputKeyT;
      value: string | number | boolean | string[] | Date | null;
    }>
  >;
};

interface Props {
  entryValue: EntryValueViewT;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function EntryValueSwitch({ entryValue, size, className }: Props) {
  // Cast to extended type to access optional properties
  const extendedEntryValue = entryValue as ExtendedEntryValue;
  const inputKey = entryValue.input_key;

  switch (inputKey) {
    case "repeatable-group":
      return (
        <RepeatableGroupEntryValue
          value={entryValue.value as string}
          jsonValue={extendedEntryValue.json_value as Array<
            Array<{
              input_label: string;
              input_id: string;
              input_key: InputKeyT;
              value:
                | string
                | number
                | boolean
                | string[]
                | Date
                | null;
            }>
          >}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "number":
      return (
        <NumberEntryValue
          value={entryValue.value as number}
          unit={extendedEntryValue.unit}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "visa-a-8":
      return (
        <VisaA8EntryValue
          value={typeof entryValue.number_value === "number"
            ? entryValue.number_value
            : entryValue.value !== null
            ? Number(entryValue.value)
            : null}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "visa-g-8":
      return (
        <VisaG8EntryValue
          value={typeof entryValue.number_value === "number"
            ? entryValue.number_value
            : entryValue.value !== null
            ? Number(entryValue.value)
            : null}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "visa-h-8":
      return (
        <VisaH8EntryValue
          value={Number(entryValue.value)}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "visa-p-8":
      return (
        <VisaP8EntryValue
          value={typeof entryValue.number_value === "number"
            ? entryValue.number_value
            : entryValue.value !== null
            ? Number(entryValue.value)
            : null}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "long-text":
      return (
        <LongTextEntryValue
          value={(entryValue.value as string) ?? "N/A"}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "duration":
      return (
        <DurationEntryValue
          value={entryValue.value as number}
          durationTimeUnit={extendedEntryValue.duration_time_unit}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "slider":
      return (
        <SliderEntryValue
          percentageValue={entryValue.value
            ? 100 * (entryValue.value as number) /
              (entryValue.inputs?.max_value ?? 100)
            : 0}
          value={entryValue.value as number}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "date-picker":
      return (
        <DatePickerEntryValue
          value={entryValue.value as string}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "date-time-picker":
      return (
        <DateTimePickerEntryValue
          value={entryValue.value as string}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "month-year":
      return (
        <MonthYearEntryValue
          value={entryValue.value as string}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "multiple-choice":
      return (
        <MultipleChoiceEntryValue
          value={entryValue.value as string[]}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "photo":
      return (
        <PhotoEntryValue
          value={entryValue.value as string}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "scale-rating":
      return (
        <ScaleRatingEntryValue
          value={entryValue.value === null ? null : Number(entryValue.value)}
          inputId={entryValue.input_id}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "foot-photos":
      return (
        <FootPhotosEntryValue
          value={entryValue.value as string}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "pain-slider":
      return (
        <PainSliderEntryValue
          value={entryValue.value as number}
          size={size}
          inputLabel={entryValue.input_label}
          className={className}
        />
      );
    case "glucose-slider":
      return (
        <GlucoseSliderEntryValue
          value={entryValue.value as number}
          size={size}
          inputLabel={entryValue.input_label}
          className={className}
        />
      );
    case "yes-no":
      return (
        <YesNoEntryValue
          value={entryValue.value as string}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    case "tried-treatment":
      return (
        <TriedTreatmentEntryValue
          value={entryValue.json_value as string}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
    default:
      return (
        <DefaultEntryValue
          value={(entryValue.value as string) ?? "N/A"}
          inputLabel={entryValue.input_label}
          size={size}
          className={className}
        />
      );
  }
}
